import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import { ProtectedRoute, AdminRoute } from "./components/ProtectedRoute";
import Header from "./components/Header";
import Navigation from "./components/Navigation";
import PlatformFooter from "./components/PlatformFooter";
import { AuthProvider } from "./contexts/AuthContext";
import ContentRuntime from "./content-editor/ContentRuntime";
import { useAuth } from "./contexts/AuthContext";
import AdminPanel from "./pages/AdminPanel";
import ContentEditorPage from "./pages/ContentEditorPage";
import AboutPage from "./pages/AboutPage";
import AuthPortal from "./pages/AuthPortal";
import CalendarPage from "./pages/Calendar";
import CreatePost from "./pages/CreatePost";
import HomePage from "./pages/HomePage";
import Knowledge from "./pages/Knowledge";
import LandingPage from "./pages/LandingPage";
import Marketplace from "./pages/Marketplace";
import MarketplacePostDetail from "./pages/MarketplacePostDetail";
import Profile from "./pages/Profile";
import RegisterInitiative from "./pages/RegisterInitiative";
import "./index.css";

const MapPage = lazy(() => import("./pages/MapPage"));

function LegacyDirectoryRedirect() {
  const { postId } = useParams();
  return <Navigate to={postId ? `/directorio/${postId}` : "/directorio"} replace />;
}

function Layout({ children }) {
  return (
    <div className="platform-layout-shell min-h-screen bg-background">
      <Header />
      <Navigation />
      <main>{children}</main>
      <PlatformFooter />
    </div>
  );
}

function RootPage() {
  const { user, isGuest, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
          <p className="mt-4 text-sm text-mutedForeground">Cargando portal principal...</p>
        </div>
      </div>
    );
  }

  if (!user && !isGuest) {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout>
      <HomePage />
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ContentRuntime />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/inicio" element={<RootPage />} />
          <Route path="/login" element={<AuthPortal mode="login" />} />
          <Route path="/register" element={<AuthPortal mode="register" />} />

          <Route
            path="/quienes-somos"
            element={
              <Layout>
                <AboutPage />
              </Layout>
            }
          />

          <Route
            path="/mapa"
            element={
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex min-h-[calc(100vh-160px)] items-center justify-center">
                      <div className="text-center">
                        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
                        <p className="mt-4 text-sm text-mutedForeground">Cargando mapa de Medellin...</p>
                      </div>
                    </div>
                  }
                >
                  <MapPage />
                </Suspense>
              </Layout>
            }
          />

          <Route
            path="/calendario"
            element={
              <Layout>
                <CalendarPage />
              </Layout>
            }
          />

          <Route
            path="/directorio"
            element={
              <Layout>
                <Marketplace />
              </Layout>
            }
          />

          <Route
            path="/mercado"
            element={<LegacyDirectoryRedirect />}
          />

          <Route
            path="/directorio/:postId"
            element={
              <Layout>
                <MarketplacePostDetail />
              </Layout>
            }
          />

          <Route
            path="/publicacion/:postId"
            element={<LegacyDirectoryRedirect />}
          />

          <Route
            path="/conocimiento"
            element={
              <Layout>
                <Knowledge />
              </Layout>
            }
          />

          <Route
            path="/registrar-iniciativa"
            element={
              <ProtectedRoute>
                <Layout>
                  <RegisterInitiative />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/crear-publicacion"
            element={
              <ProtectedRoute>
                <Layout>
                  <CreatePost />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Layout>
                  <AdminPanel />
                </Layout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/contenido"
            element={
              <AdminRoute>
                <ContentEditorPage />
              </AdminRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
